import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { SubmitHandler, useForm, Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { zodResolver } from '@hookform/resolvers/zod';

import { Badge } from 'components/UI/Badge';
import { Button } from 'components/UI/Button';
import { Heading } from 'components/UI/Heading';
import { InputField } from 'components/UI/InputField';
import { SelectField, OptionType } from 'components/UI/SelectField';
import { SuccessState } from 'components/UI/SuccessState';
import { useBrand } from 'hooks/useBrand';
import { useTranslations } from 'hooks/useTranslations';
import * as routes from 'router/routes';
import {
  useGetCountriesQuery,
  useGetLanguagesQuery,
  useCreateUserMutation
} from 'store/diamApi';
import errorCodes from 'assets/data/errorCodes.json';
import { groupSelector } from 'store/groupState/groupState';
import { localizationSelector } from 'store/localizationState';
import { showErrorToast } from 'utils/toastUtils';
import { privacyPolicyUrl, termsOfUseUrl } from 'utils/constants';
import { CreateUserData } from 'models/diam';
import { JsonApiError } from 'models/jsonApiError';
import { getSignUpPageSchema, SignUpFormData } from './schema';
import {
  FormContainer,
  InputGroup,
  Row,
  PillsContainer,
  Description,
  DescriptionText,
  Link,
  GroupLabel
} from './styled';

export const SignUpPage: FC = () => {
  const t = useTranslations();
  const [emailAddress, setEmailAddress] = useState<string | null>(null);
  const { languageCode } = useSelector(localizationSelector);
  const { group: groupParam } = useSelector(groupSelector);
  const { brand } = useBrand();

  // RTK Mutation
  const [createUser, createUserResult] = useCreateUserMutation();

  // Fetch options
  const { data: countries } = useGetCountriesQuery();
  const { data: languages } = useGetLanguagesQuery();

  const countryOptions: OptionType[] = useMemo(
    () =>
      countries?.map((c) => ({
        value: c.countryCode,
        label: c.name
      })) ?? [],
    [countries]
  );

  const languageOptions: OptionType[] = useMemo(
    () =>
      languages?.map((l) => ({
        value: l.langCode,
        label: l.name
      })) ?? [],
    [languages]
  );

  // Zod schema
  const schema = useMemo(() => getSignUpPageSchema(t), [t]);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    getValues,
    formState: { errors }
  } = useForm<SignUpFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      language: languageCode,
      group: groupParam || undefined
    }
  });

  // Sync language with global state
  useEffect(() => {
    if (languageCode) {
      setValue('language', languageCode);
    }
  }, [languageCode, setValue]);

  // Set default country and language from browser locale if not already set
  useEffect(() => {
    if (languageOptions.length > 0) {
      // If language not set, try browser locale
      if (!getValues('language')) {
        const browserLocale = navigator.language.split('-')[0];
        const browserLanguage = languageOptions.find(
          (l) => l.value === browserLocale
        );
        if (browserLanguage) {
          setValue('language', browserLanguage.value);
        } else {
          setValue('language', languageOptions[0].value);
        }
      }

      // If country not set, try browser locale
      if (!getValues('country')) {
        const locale = navigator.language;
        if (locale.includes('-')) {
          const detectedCountry = locale.split('-')[1].toUpperCase();
          setValue('country', detectedCountry);
        }
      }
    }
  }, [languageOptions, setValue, getValues]);

  const onSubmit: SubmitHandler<SignUpFormData> = useCallback(
    async (data) => {
      setEmailAddress(data.email);

      const { group, ...rest } = data;
      const payload: CreateUserData = {
        user: {
          ...rest,
          username: data.email,
          ...(group ? { groups: [group] } : {})
        },
        meta: {
          brand: brand
        }
      };

      await createUser(payload);
    },
    [createUser, brand]
  );

  // Edge case handling for error from API, shows the detailed error if available else generic one.
  useEffect(() => {
    const error = createUserResult.error as JsonApiError;
    const unhandledErrorCodes = ['invalid.request', 'invalid.characters'];

    if (error?.code) {
      if (unhandledErrorCodes.includes(error.code) || !(error.code in errorCodes)) {
        const message = error.detail || t('sign_up.error_message');

        showErrorToast(message);
        createUserResult.reset();
      }
    }
  }, [createUserResult.error, createUserResult.reset, t]);

  if (createUserResult.isSuccess) {
    return (
      <SuccessState
        hideBackButton={true}
        iconType="email"
        heading={t('sign_up.success_message')}
        details={
          <Description $center>
            <DescriptionText>
              {t('sign_up.success_description')}
            </DescriptionText>
            <DescriptionText>
              <strong>{emailAddress}</strong>
            </DescriptionText>
          </Description>
        }
        link={{ url: routes.LOGIN, text: t('general.go_to_login') }}
      />
    );
  }

  return (
    <>
      <Heading>{t('sign_up.title')}</Heading>
      <FormContainer onSubmit={handleSubmit(onSubmit)}>
        <InputGroup>
          <InputField
            id="email"
            label={t('sign_up.email_address')}
            required
            type="email"
            placeholder={t('sign_up.email_address_placeholder')}
            error={errors.email}
            register={register('email')}
            autoComplete="off"
          />

          <Row>
            <InputField
              id="firstName"
              label={t('sign_up.first_name')}
              required
              placeholder={t('sign_up.first_name_placeholder')}
              error={errors.firstName}
              register={register('firstName')}
              autoComplete="off"
            />

            <InputField
              id="lastName"
              label={t('sign_up.last_name')}
              required
              placeholder={t('sign_up.last_name_placeholder')}
              error={errors.lastName}
              register={register('lastName')}
              autoComplete="off"
            />
          </Row>

          <Row>
            <InputField
              id="password"
              label={t('sign_up.password')}
              required
              isPasswordInput
              placeholder={t('sign_up.password_placeholder')}
              error={errors.password}
              register={register('password')}
              autoComplete="new-password"
            />
          </Row>

          <Row>
            <InputField
              id="confirmPassword"
              label={t('sign_up.confirm_password')}
              required
              isPasswordInput
              placeholder={t('sign_up.confirm_password_placeholder')}
              error={errors.confirmPassword}
              register={register('confirmPassword')}
              autoComplete="new-password"
            />
          </Row>

          <Row>
            <Controller
              name="country"
              control={control}
              render={({ field }) => (
                <SelectField
                  id="country"
                  label={t('sign_up.country')}
                  required
                  enableSearch
                  options={countryOptions}
                  error={errors.country}
                  onSelectValue={field.onChange}
                  defaultValue={field.value}
                  autoComplete="new-password" // hack to disable autocomplete for select dropdown
                />
              )}
            />

            <Controller
              name="language"
              control={control}
              render={({ field }) => (
                <SelectField
                  id="language"
                  label={t('sign_up.language')}
                  required
                  enableSearch
                  options={languageOptions}
                  error={errors.language}
                  onSelectValue={field.onChange}
                  defaultValue={field.value}
                  autoComplete="new-password" // hack to disable autocomplete for select dropdown
                />
              )}
            />
          </Row>

          {groupParam && (
            <div>
              <GroupLabel>
                {t('sign_up.group')}
              </GroupLabel>
              <PillsContainer>
                <Badge variant="light">{groupParam}</Badge>
              </PillsContainer>
              <input type="hidden" {...register('group')} />
            </div>
          )}
        </InputGroup>

        <Description>
          <DescriptionText>
            {t('sign_up.registration_disclaimer')}
          </DescriptionText>
          <DescriptionText>
            {`${t('sign_up.terms_notice_1')} `}
            <Link
              href={termsOfUseUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('footer.terms_of_use')}
            </Link>
            {` ${t('sign_up.terms_notice_2')} `}
            <Link
              href={privacyPolicyUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('footer.privacy_policy')}
            </Link>
            .
          </DescriptionText>
        </Description>

        <Button type="submit" disabled={createUserResult.isLoading}>
          {t('sign_up.submit')}
        </Button>
      </FormContainer>
    </>
  );
};
